import {DateTime} from 'luxon';
import {CustomerBankAccount} from '../../../api/customerBankAccounts/CustomerBankAccount.ts';
import {isPaymentProviderInstitution, PAYMENT_MOLLIE_INSTITUTION_ID} from '../../../lib/institutionDisplayNames.ts';
import {MoneyLabel} from '../../general/MoneyLabel.tsx';
import {balanceTextSizeClass} from './balanceTextSize.ts';
import {CardFront} from './CardFront.tsx';
import {paletteSeed} from './cardIdentity.ts';
import {MastercardMark} from './CardMarks.tsx';
import {captionFor, ConnectionPanel, StatusIndicator} from './ConnectionPanel.tsx';
import {ConnectionStatus, formatLastSynced, keepsPanelOpen, resolveConnectionStatus} from './connectionStatus.ts';
import {formatIban} from './formatIban.ts';
import mollieLogoUrl from './icons/mollie-logo.svg';
import {bankDisplayName} from './knownBanks.ts';
import {getBankCardSkin} from './skins';

function StatusNotice({status, onRenew}: {status: ConnectionStatus; onRenew?: () => void}) {
    switch (status) {
        case ConnectionStatus.Expired:
            return (
                <p className="mt-2.5 truncate px-1 text-[13px] font-medium text-red-600">
                    Koppeling verlopen. Koppel opnieuw om bij te werken.
                </p>
            );
        case ConnectionStatus.Expiring:
            return (
                <p className="mt-2.5 truncate px-1 text-[13px] font-medium text-amber-700">
                    Koppeling verloopt binnenkort.{' '}
                    {onRenew ? (
                        <button
                            type="button"
                            onClick={onRenew}
                            className="font-semibold underline underline-offset-2 hover:text-amber-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                        >
                            Vernieuw de koppeling
                        </button>
                    ) : (
                        'Vernieuw de koppeling.'
                    )}
                </p>
            );
        default:
            return null;
    }
}

interface Props {
    customerBankAccount: CustomerBankAccount;
    onRenewButtonClick?: (bankId: string) => void;
}

export function BankAccountCard({customerBankAccount: account, onRenewButtonClick}: Props) {
    const name = bankDisplayName(account.institutionName);
    const skin = getBankCardSkin(account.institutionName, paletteSeed(account.institutionName, name, account.id));
    const lastSynced = formatLastSynced(account.refreshedOn, DateTime.now());
    const isPaymentProvider = isPaymentProviderInstitution(account.institutionName);
    const status = resolveConnectionStatus({
        accountType: account.accountType,
        isExpired: account.isExpired,
        expiresSoon: account.expiresSoon,
        isPaymentProvider,
    });
    const isExpired = status === ConnectionStatus.Expired;
    const iban = account.hasIban() ? formatIban(account.iban) : '';
    const logoUrl = account.institutionName === PAYMENT_MOLLIE_INSTITUTION_ID ? mollieLogoUrl : account.logoUrl;
    const renewConnection = onRenewButtonClick && (() => onRenewButtonClick(account.institutionName));

    return (
        <CardFront
            skin={skin}
            brandProps={{logoUrl, institutionDisplayName: name}}
            fadeTextOnHover={true}
            hideDetails={keepsPanelOpen(status)}
            ariaLabel={`${name}: ${captionFor(status, lastSynced)}`}
            isMuted={isExpired}
            balanceLabel={<><StatusIndicator status={status} />Saldo</>}
            balance={isExpired
                ? 'Niet beschikbaar'
                : <MoneyLabel value={account.balance / 100} currency={account.currency || 'EUR'} showCurrency />}
            balanceSizeClass={balanceTextSizeClass(account.balance)}
            holderName={account.ownerName}
            accountLine={iban}
            networkMark={skin.hasMastercard && <MastercardMark wordColor={skin.mastercardWordColor} className="w-full" />}
            footer={<StatusNotice status={status} onRenew={renewConnection} />}
            overlay={
                <ConnectionPanel
                    status={status}
                    holderName={account.ownerName}
                    accountLine={iban}
                    lastSynced={lastSynced}
                    connectionLabel={isPaymentProvider ? 'Koppeling' : 'PSD2-koppeling'}
                    onAction={renewConnection}
                />
            }
        />
    );
}
